import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuditEntrySchema } from './audit.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { GoodsReceiptStatsSchema } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, UnitPrecisionSchema, } from './primitives.js';
const c = initContract();
/**
 * Goods Received Notes (PUR-02) — "what actually arrived from a supplier,
 * and whether it was any good", written while the lorry is being unloaded.
 *
 * Raised AGAINST a purchase order (BR-PUR-02-13: a real reference). Each
 * line carries the quality check AND an accepted quantity — BR-PUR-02-04's
 * resolved ambiguity: Accepted = all of it, Rejected = none ("excluded from
 * stock", BR-PUR-02-03), Partially Accepted = the typed number.
 *
 * POSTING (BR-PUR-02-08, the draft-to-posted boundary):
 *   - one 'purchase' movement per accepted quantity, valued at the ORDER's
 *     price (BR-PUR-02-06) — serial-tracked lines write one movement per
 *     serial;
 *   - the PUR-02 §12 entry: DR 1131 Inventory / CR 2115 Goods Received Not
 *     Invoiced. No tax — "tax belongs to the bill";
 *   - the order's received figures + partially_received/received status
 *     (BR-PUR-02-02), and the bill's three-way received leg goes live.
 *
 * Permission area: purchasing (PART 7 §9 leaves "who may post a receipt" to
 * PART 16's role grid; the twelve-area vocabulary puts every purchasing
 * document under `purchasing.*`, posting = the approve action, the same
 * pattern as bills).
 */
// ── The fixed sentences of PUR-02 ───────────────────────────────────────────
export const GRN_MESSAGES = {
    /** BR-PUR-02-05 / ERR-PUR-02-02, quoted exactly. */
    overReceipt: (ordered, code, arrived) => `You ordered ${ordered} of ${code} and ${arrived} have arrived. Accept the extra?`,
    /** ERR-PUR-02-03, quoted exactly. */
    partialNeedsQty: (received) => `How many of the ${received} are accepted?`,
    /** ERR-PUR-02-04. */
    alreadyPosted: 'This receipt has already been posted.',
    /** R8 / BR-PUR-02-08 — the posted lock. */
    postedLocked: 'This receipt is posted and locked. Raise a purchase return to correct it.',
    /** Only a draft moves on. */
    notADraft: 'Only a draft receipt can change. This one has already moved on.',
    /** Receiving needs a commitment: the supplier ships against a SENT order. */
    orderNotReceivable: (code) => `${code} has not been sent to the supplier, so nothing can arrive against it.`,
    /** BR-PUR-01-08's shape for receiving. */
    orderClosed: (code) => `${code} is closed. It cannot be received or billed again.`,
    /** Posting with nothing received (PUR-02 §15 "Zero" is per line, not the whole). */
    nothingReceived: 'Nothing has been received. Type the quantities that arrived first.',
    /** Loading a fully received order — the duplicate-receipt cap at zero. */
    nothingLeftToReceive: (code) => `${code} is fully received. There is nothing left to receive.`,
    /** PUR-02 §15 "Negative": goods going back are a purchase return. */
    negativeQty: 'A received quantity cannot be negative. Goods going back are a purchase return.',
    /** A GRN line must name an order line that exists on ITS order. */
    lineNotOnOrder: (lineNo) => `Line ${lineNo} is not on the purchase order this receipt is against.`,
    /** Only item-linked order lines are receivable (BR-MD-03-07's document face). */
    freeTextNotReceivable: (description) => `"${description}" is not a catalogue item. Only item lines can be received into stock.`,
    /** Wave 5C — a line on a receipt AGAINST an order must name the order line it receives. */
    lineNeedsOrderLine: 'Every line on a receipt against a purchase order must name the order line it receives.',
    /**
     * Wave 5C — a receipt WITHOUT a purchase order. Its own switch
     * (`purchasing.receive_without_order`): the goods, the supplier and the
     * cost are typed at the door with nothing upstream vouching for them.
     */
    directNeedsPermission: 'Receiving goods without a purchase order needs its own permission. Ask your administrator.',
    directNeedsPartner: 'A receipt without a purchase order must name the supplier.',
    freeLineNeedsItem: 'Every line on a receipt without a purchase order must name a catalogue item.',
    orderLineOnDirectReceipt: 'This receipt has no purchase order, so its lines cannot name order lines.',
    /**
     * Wave 5C — an import order's landed cost has been posted onto this
     * receipt's stock; mirroring the receipt would take the charges off the
     * shelf a second time, so the correction is a purchase return.
     */
    landedCostAttached: 'Landed cost from an import order has been added to this receipt, so it cannot be cancelled. Raise a purchase return instead.',
    /**
     * BR-PUR-02-09 / ERR-PUR-02-06, quoted exactly. PUR-02 §14: "Cancelled a
     * receipt whose bill is posted | 'Bill BILL-4120 is matched to this receipt.
     * Cancel the bill first.' | Cancel the bill".
     *
     * The bill CODE, not its id — the sentence is only actionable if the reader
     * can go and find the bill it names, which is why this takes the formatted
     * number rather than composing one here.
     */
    billMatched: (billCode) => `Bill ${billCode} is matched to this receipt. Cancel the bill first.`,
    /** Only the posted state cancels (GEN-70; a draft deletes instead). */
    cancelOnlyPosted: 'Only a posted receipt can be cancelled. A draft is edited or deleted instead.',
    /** GEN-72: the cancelling act carries its reason. */
    cancelReasonRequired: 'A posted receipt can only be cancelled with a reason, which is written to the audit log.',
};
/**
 * D46 — the GRN family's structured refusals: key + params beside the
 * composed English sentence, so a refusal that names an order, a bill, a
 * quantity or a line reaches an Arabic reader in Arabic. One builder per
 * parameterised member the API raises inside an HTTP exception body — the
 * raise sites throw THESE, and each `message` is composed by the
 * `GRN_MESSAGES` twin so the two cannot drift (parity pinned member by
 * member in `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * Every param is a display value — a formatted document code, a decimal
 * quantity string in the item's own unit precision, a line's description, or
 * a line NUMBER (a label, not a count: nothing pluralises on it). No member
 * carries a select token and none carries a count, so both dictionaries
 * interpolate every param verbatim.
 */
export const GRN_REFUSALS = {
    overReceipt: (ordered, code, arrived) => ({
        message: GRN_MESSAGES.overReceipt(ordered, code, arrived),
        key: 'grn.errOverReceipt',
        params: { ordered, code, arrived },
    }),
    partialNeedsQty: (received) => ({
        message: GRN_MESSAGES.partialNeedsQty(received),
        key: 'grn.errPartialNeedsQty',
        params: { received },
    }),
    orderNotReceivable: (code) => ({
        message: GRN_MESSAGES.orderNotReceivable(code),
        key: 'grn.errOrderNotReceivable',
        params: { code },
    }),
    orderClosed: (code) => ({
        message: GRN_MESSAGES.orderClosed(code),
        key: 'grn.errOrderClosed',
        params: { code },
    }),
    nothingLeftToReceive: (code) => ({
        message: GRN_MESSAGES.nothingLeftToReceive(code),
        key: 'grn.errNothingLeftToReceive',
        params: { code },
    }),
    lineNotOnOrder: (lineNo) => ({
        message: GRN_MESSAGES.lineNotOnOrder(lineNo),
        key: 'grn.errLineNotOnOrder',
        params: { lineNo },
    }),
    freeTextNotReceivable: (description) => ({
        message: GRN_MESSAGES.freeTextNotReceivable(description),
        key: 'grn.errFreeTextNotReceivable',
        params: { description },
    }),
    billMatched: (billCode) => ({
        message: GRN_MESSAGES.billMatched(billCode),
        key: 'grn.errBillMatched',
        params: { billCode },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const GrnQualitySchema = z.enum(['accepted', 'rejected', 'partially_accepted']);
export const GrnStatusSchema = z.enum(['draft', 'posted', 'cancelled']);
/** A received quantity: zero legal (a short shipment), never negative. */
const ReceivedQtySchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Quantities are decimal strings with at most 3 decimals');
/** A unit cost as typed on a FREE line: base currency per stock unit, 6 dp. */
const UnitCostInputSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,6})?$/, 'A unit cost is a decimal string with at most 6 decimals');
export const GrnLineInputSchema = z
    .object({
    /**
     * The order line being received, by its line number on the order.
     * Required on a receipt against an order; ABSENT on a free line of a
     * receipt without one (wave 5C), where `itemId` names the goods instead.
     */
    poLineNo: z.number().int().positive().optional(),
    /** Wave 5C — a free line's item (a receipt without an order only). */
    itemId: z.uuid().optional(),
    /**
     * Wave 5C — what a free line's goods enter stock at. Absent = the item's
     * last POSTED bill price in the base currency, else its cost price. Never
     * read on an order line, whose cost is the ORDER's (BR-PUR-02-06).
     */
    unitCost: UnitCostInputSchema.optional(),
    /** Defaults to the receipt's header warehouse. */
    warehouseId: z.uuid().optional(),
    qtyReceived: ReceivedQtySchema,
    quality: GrnQualitySchema.optional(),
    /** Required (and only meaningful) when quality is partially_accepted. */
    acceptedQty: ReceivedQtySchema.optional(),
    /** BR-MD-03 tracking capture: one serial per ACCEPTED unit. */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    note: z.string().max(500).optional(),
})
    .strict();
export const GrnLineSchema = z
    .object({
    id: z.uuid(),
    lineNo: z.number().int(),
    /** Both null on a free line of a receipt without an order (wave 5C). */
    poLineId: z.uuid().nullable(),
    poLineNo: z.number().int().nullable(),
    itemId: z.uuid(),
    itemCode: z.string(),
    description: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    qtyOrdered: z.string(),
    /**
     * Wave 1C — Σ received on the order line over OTHER posted receipts
     * (this receipt excluded), and what is still outstanding after them:
     * ordered − alreadyReceived, floored at zero. The grid draws Ordered /
     * Received before / Remaining, and the backorder flag compares this
     * receipt's received figure against `remainingQty`, not `qtyOrdered`.
     */
    alreadyReceivedQty: z.string(),
    remainingQty: z.string(),
    qtyReceived: z.string(),
    quality: GrnQualitySchema,
    acceptedQty: z.string(),
    /**
     * `N-4` — the unit ALL THREE figures on this row are in, and its
     * decimals. `goods_receipt_lines` has no `unit_id`, so ordered, received
     * and accepted are the item's STOCK unit alike; one pair covers the row.
     *
     * Resolved on the server rather than looked up by the screen, exactly as
     * `InvoiceLineSchema` resolves its own: the printed GRN and the grid must
     * not be able to disagree about what "200" means.
     */
    unitSymbol: z.string(),
    unitPrecision: UnitPrecisionSchema,
    /** The ORDER's net unit price — what the goods entered stock at. */
    unitCost: z.string(),
    lineValue: z.string(),
    /** BR-PUR-03-04: accepted quantity already relieved by posted bills. */
    invoicedQty: z.string(),
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
    note: z.string().nullable(),
    /** Backorder chip: received < REMAINING on THIS line (amber row). */
    backorder: z.boolean(),
})
    .strict();
/** Wave 5C — the header fields as STORED (nullable), shared by the response. */
export const GrnHeaderFieldsSchema = z
    .object({
    supplierDeliveryNoteNo: z.string().nullable(),
    /** Defaults to the creator, then to the poster when still unnamed. */
    receivedByUserId: z.uuid().nullable(),
    checkedByUserId: z.uuid().nullable(),
    driver: z.string().nullable(),
    vehicleNo: z.string().nullable(),
    manualRef: z.string().nullable(),
    bookNo: z.string().nullable(),
})
    .strict();
/** Wave 5C — the same fields as INPUT: optional on create, nullable-optional on update. */
const GrnHeaderInputSchema = {
    supplierDeliveryNoteNo: z.string().trim().min(1).max(60),
    receivedByUserId: z.uuid(),
    checkedByUserId: z.uuid(),
    driver: z.string().trim().min(1).max(120),
    vehicleNo: z.string().trim().min(1).max(60),
    manualRef: z.string().trim().min(1).max(60),
    bookNo: z.string().trim().min(1).max(40),
};
export const GoodsReceiptSchema = z
    .object({
    id: z.uuid(),
    /** GRN-#### — one sequence (open question 3's default). */
    code: z.string(),
    receiptNo: z.number().int(),
    status: GrnStatusSchema,
    receiptDate: IsoDateSchema,
    /**
     * BR-PUR-02-11 — "an actual delivery date later than the expected date
     * must be flagged, so late suppliers can be found." This is the linked
     * order's promised date (purchase_orders.expected_date, BR-PUR-01-10's
     * field), carried onto the receipt so the screens can compare the two
     * without a second fetch; null when the order names no expected date, in
     * which case nothing can be late. The flag itself is derived where it is
     * drawn (`receiptIsLate`, ISO dates compare as strings) — receiptDate
     * AFTER expectedDate, strictly: arriving ON the promised day is on time.
     */
    expectedDate: IsoDateSchema.nullable(),
    /**
     * Wave 5C — null on a receipt WITHOUT an order (`direct`), whose code
     * reads as '' and whose lines are free lines. BR-PUR-02-13's "real
     * reference" still holds whenever an order IS named.
     */
    purchaseOrderId: z.uuid().nullable(),
    purchaseOrderCode: z.string(),
    direct: z.boolean(),
    /**
     * Wave 5C — the import order whose allocated landed cost was posted onto
     * this receipt's stock (value-only `landed_cost` movements); null when
     * no import touched it.
     */
    importOrderId: z.uuid().nullable(),
    partnerId: z.uuid(),
    partnerCode: z.string(),
    partnerName: z.string(),
    warehouseId: z.uuid(),
    warehouseName: z.string(),
    supplierRef: z.string().nullable(),
    /**
     * Wave 5C — the lorry's paper. The supplier's DELIVERY NOTE number is a
     * different paper from their invoice (`supplierRef`, the bill's match
     * key) and often weeks apart; who received and who checked are people
     * (ids + display names, resolved server-side so the screen and the PDF
     * print the same name); driver / vehicle are the stock transfer's
     * logistics pair; manual ref / book no. are the paper GRN book's own.
     */
    ...GrnHeaderFieldsSchema.shape,
    receivedByName: z.string().nullable(),
    checkedByName: z.string().nullable(),
    lines: GrnLineSchema.array(),
    /** Footer: QTY ORDERED / QTY RECEIVED / total accepted value. */
    totalOrdered: z.string(),
    totalReceived: z.string(),
    totalAccepted: z.string(),
    acceptedValue: z.string(),
    /**
     * S3's rejected note — ERR-PUR-02-07, and the WORDING IS DELIBERATELY NOT
     * the spec's example sentence.
     *
     * PUR-02 §14 shows "1 item(s) rejected — excluded from stock", and
     * BR-PUR-02-12 is the rule about that very sentence: "The rejected-items
     * note counts lines. It must count units, or say 'lines'." The spec is
     * naming its own example as the defect and offering two repairs. This
     * build takes the SECOND — the note says `line(s)` (see `grn.rejectedNote`
     * in both locales) and this field is named `rejectedLineCount` to match.
     *
     * So a reviewer comparing the screen against §14 will find the words
     * differ, and that is the rule being obeyed rather than broken. Copying
     * the example verbatim is the one thing BR-PUR-02-12 forbids.
     */
    rejectedLineCount: z.number().int(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /** GEN-72's stamp trio — set once by cancel, null otherwise. */
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    cancellationJournalEntryId: z.uuid().nullable(),
    createdAt: z.string(),
})
    .strict();
export const CreateGrnRequestSchema = z
    .object({
    /**
     * The order received against. ABSENT = a receipt WITHOUT an order
     * (wave 5C): `partnerId` is then required, `lines` are free lines, and
     * the caller must hold `purchasing.receive_without_order`.
     */
    purchaseOrderId: z.uuid().optional(),
    /** The supplier — read off the order when one is named, required otherwise. */
    partnerId: z.uuid().optional(),
    receiptDate: IsoDateSchema,
    warehouseId: z.uuid(),
    supplierRef: z.string().trim().min(1).max(60).optional(),
    supplierDeliveryNoteNo: GrnHeaderInputSchema.supplierDeliveryNoteNo.optional(),
    receivedByUserId: GrnHeaderInputSchema.receivedByUserId.optional(),
    checkedByUserId: GrnHeaderInputSchema.checkedByUserId.optional(),
    driver: GrnHeaderInputSchema.driver.optional(),
    vehicleNo: GrnHeaderInputSchema.vehicleNo.optional(),
    manualRef: GrnHeaderInputSchema.manualRef.optional(),
    bookNo: GrnHeaderInputSchema.bookNo.optional(),
    /**
     * Absent = every item-linked order line at its remaining quantity ("Load
     * from PO"); on a receipt without an order, absent = an empty draft the
     * clerk fills in the grid.
     */
    lines: GrnLineInputSchema.array().optional(),
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((body) => body.purchaseOrderId !== undefined || body.partnerId !== undefined, {
    message: GRN_MESSAGES.directNeedsPartner,
    path: ['partnerId'],
});
export const UpdateGrnRequestSchema = z
    .object({
    receiptDate: IsoDateSchema.optional(),
    warehouseId: z.uuid().optional(),
    supplierRef: z.string().trim().min(1).max(60).nullable().optional(),
    supplierDeliveryNoteNo: GrnHeaderInputSchema.supplierDeliveryNoteNo.nullable().optional(),
    receivedByUserId: GrnHeaderInputSchema.receivedByUserId.nullable().optional(),
    checkedByUserId: GrnHeaderInputSchema.checkedByUserId.nullable().optional(),
    driver: GrnHeaderInputSchema.driver.nullable().optional(),
    vehicleNo: GrnHeaderInputSchema.vehicleNo.nullable().optional(),
    manualRef: GrnHeaderInputSchema.manualRef.nullable().optional(),
    bookNo: GrnHeaderInputSchema.bookNo.nullable().optional(),
    /** Present = replace the whole grid (draft only). */
    lines: GrnLineInputSchema.array().optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
export const CancelGrnRequestSchema = z
    .object({
    /** GEN-72: cancelling must ask for a reason and store it. */
    reason: z.string().trim().min(1, GRN_MESSAGES.cancelReasonRequired).max(500),
})
    .strict();
/**
 * Wave 5C — the receipt screen's history card: the shared audit log filtered
 * to THIS receipt, the bill card's shape (PUR-03 §5e / SAL-04 §5e: up to six
 * entries, newest first). The cap is the server's promise, as it is there.
 */
export const GRN_HISTORY_LIMIT = 6;
export const GrnHistorySchema = z
    .object({ entries: AuditEntrySchema.array().max(GRN_HISTORY_LIMIT) })
    .strict();
const receiptParams = z.object({ receiptId: z.uuid() }).strict();
export const goodsReceiptsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/purchases/receipts',
        query: pagedQuery({
            purchaseOrderId: z.uuid().optional(),
            /** Wave 2 — the supplier, for the list's filter and the supplier card's drill-through. */
            partnerId: z.uuid().optional(),
            status: GrnStatusSchema.optional(),
            /**
             * The list's search box (and the header rail's Find): the printed
             * number, the supplier's name through `ar_norm`, and field 9's
             * supplier reference (their invoice number).
             */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the receipt date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('receipts', GoodsReceiptSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Goods received notes (purchasing.view), PAGED (NFR-42) — both filters and the search in SQL.',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail: the goods receipts on either side of
     * this one in number order (null at an end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/purchases/receipts/:receiptId/neighbours',
        pathParams: receiptParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next goods receipt by number (purchasing.view).',
    },
    /** S4 — the "Go to no." box: one goods receipt by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/purchases/receipts/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One goods receipt by its printed number (purchasing.view).',
    },
    get: {
        method: 'GET',
        path: '/api/purchases/receipts/:receiptId',
        pathParams: receiptParams,
        responses: {
            200: GoodsReceiptSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One receipt with its quality-checked lines (purchasing.view).',
    },
    history: {
        method: 'GET',
        path: '/api/purchases/receipts/:receiptId/history',
        pathParams: receiptParams,
        responses: {
            200: GrnHistorySchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: "Wave 5C: the receipt screen's history card — the shared audit log filtered to THIS receipt, up to six entries, newest first (the bill card's shape). Gated by purchasing.view, the receipt's own door; admin.read_audit_log does not bind here.",
    },
    create: {
        method: 'POST',
        path: '/api/purchases/receipts',
        body: CreateGrnRequestSchema,
        responses: {
            201: GoodsReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New draft GRN against a SENT order (purchasing.create) — lines default to the order lines at their remaining quantities, the Load from PO behavior — or, with no order and a supplier (wave 5C, purchasing.receive_without_order), a direct receipt of free lines.',
    },
    update: {
        method: 'PATCH',
        path: '/api/purchases/receipts/:receiptId',
        pathParams: receiptParams,
        body: UpdateGrnRequestSchema,
        responses: {
            200: GoodsReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a draft receipt (purchasing.edit); posted answers 409 (BR-PUR-02-08).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/purchases/receipts/:receiptId',
        pathParams: receiptParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a draft receipt (purchasing.delete); posted answers 409.',
    },
    post: {
        method: 'POST',
        path: '/api/purchases/receipts/:receiptId/post',
        pathParams: receiptParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: GoodsReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post — add to stock (purchasing.approve): purchase movements for ACCEPTED quantities at the order price, DR 1131 / CR 2115, order received figures + status. Once only (BR-PUR-02-07).',
    },
    cancel: {
        method: 'POST',
        path: '/api/purchases/receipts/:receiptId/cancel',
        pathParams: receiptParams,
        body: CancelGrnRequestSchema,
        responses: {
            200: GoodsReceiptSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Cancel a POSTED receipt (purchasing.approve — BR-PUR-02-08's tail): opposite movements at the cost the goods entered at, the §12 entry reversed on the receipt's own date (GEN-72), the order's received figures restored. Refused once a posted bill matched (BR-PUR-02-09) and into a closed period (GEN-73/BR-PUR-02-10). Reason required and audited.",
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/purchases/receipts/stats',
        responses: {
            200: GoodsReceiptStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'PUR-02 list tiles (purchasing.view): drafts to post, received this week, sent orders awaiting receipt, rejected quantity over posted receipts — in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=goods-receipts.js.map